import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { formatCurrency } from '../../shared/utils/format';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Input,
  Select,
  LoadingSkeleton,
} from '@nexora/design-system';

/* ── Contratos reais (backend/.review-loop/reports/api-contracts.md §7.1) ──
 * GET /products?tenantId= → ProductResponseDto[] (array, SEM paginação servidor;
 *                         busca paginada só via /products/search, sombreada — §2.1)
 * POST /products          → cria produto (201)
 * Como o GET lista não pagina no servidor, a paginação desta tela é
 * client-side — declarado visivelmente no rodapé (ver A9 no evidence).
 */

interface Product {
  id: string;
  type: string;
  sku: string;
  name: string;
  price: number;
  costPrice?: number;
  unit: string;
  ncmCode?: string;
  categoryId?: string;
  status: string;
}

const STATUS_META: Record<string, { variant: 'success' | 'neutral' | 'error'; label: string }> = {
  ACTIVE: { variant: 'success', label: 'Ativo' },
  INACTIVE: { variant: 'neutral', label: 'Inativo' },
  DISCONTINUED: { variant: 'error', label: 'Descontinuado' },
};

function statusMeta(status: string): { variant: 'success' | 'neutral' | 'error'; label: string } {
  return STATUS_META[status] ?? { variant: 'neutral', label: status };
}

const PAGE_SIZE = 20;

export function ProductsPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [copiedSku, setCopiedSku] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery<Product[]>({
    queryKey: queryKeys.masterData.products(tenantId),
    queryFn: async () => apiClient.get<Product[]>('/products', { params: { tenantId } }),
    enabled: !!tenantId,
  });

  const products = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const kpis = useMemo(() => {
    const total = products.length;
    const active = products.filter((p) => p.status === 'ACTIVE').length;
    const inactive = products.filter((p) => p.status !== 'ACTIVE').length;
    const avg = total > 0 ? products.reduce((a, p) => a + (Number(p.price) || 0), 0) / total : 0;
    return { total, active, inactive, avg };
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (statusFilter !== 'ALL' && p.status !== statusFilter) return false;
      if (!q) return true;
      return `${p.name} ${p.sku} ${p.ncmCode ?? ''}`.toLowerCase().includes(q);
    });
  }, [products, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageItems = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const from = filtered.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const to = Math.min(filtered.length, safePage * PAGE_SIZE + PAGE_SIZE);

  async function handleCopySku(sku: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(sku);
      setCopiedSku(sku);
      window.setTimeout(() => setCopiedSku((cur) => (cur === sku ? null : cur)), 2000);
    } catch {
      setCopiedSku(null);
    }
  }

  return (
    <div className="products-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="eyebrow">Nexora · Cadastros</p>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Produtos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Produtos, SKU e categorias.</p>
        </div>
        <div className="flex flex-wrap items-center gap-sm">
          <Button variant="primary" onClick={() => navigate({ to: '/master-data/products/new' })}>
            + Novo Produto
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="kpi-grid" aria-busy="true" aria-live="polite">
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
          <LoadingSkeleton width="100%" height="110px" />
        </div>
      ) : error ? (
        <Card>
          <div className="error-state" role="alert">
            <p>Erro ao carregar produtos (GET /products).</p>
            <p className="error-detail">{(error as Error).message}</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="kpi-grid" aria-live="polite">
            <Card>
              <div className="kpi-card">
                <p className="kpi-value">{kpis.total}</p>
                <p className="kpi-label">Produtos cadastrados</p>
                <p className="kpi-source">Fonte: contagem real do array GET /products</p>
              </div>
            </Card>
            <Card>
              <div className="kpi-card">
                <p className="kpi-value">{kpis.active}</p>
                <p className="kpi-label">Ativos</p>
                <p className="kpi-source">Fonte: status === ACTIVE no array real</p>
              </div>
            </Card>
            <Card>
              <div className="kpi-card">
                <p className="kpi-value">{kpis.inactive}</p>
                <p className="kpi-label">Inativos / descontinuados</p>
                <p className="kpi-source">Fonte: status ≠ ACTIVE no array real</p>
              </div>
            </Card>
            <Card>
              <div className="kpi-card">
                <p className="kpi-value">{formatCurrency(kpis.avg)}</p>
                <p className="kpi-label">Preço médio de venda</p>
                <p className="kpi-source">Média da página atual (lista completa carregada)</p>
              </div>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <h3 className="exec-title">Catálogo</h3>
              <p className="exec-subtitle">Busca e status filtram client-side sobre a lista carregada</p>
            </CardHeader>
            <CardBody>
              <div className="filters">
                <div className="filter-search">
                  <Input
                    label=""
                    placeholder="Buscar por nome, SKU ou NCM..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(0);
                    }}
                  />
                </div>
                <div className="filter-status">
                  <Select
                    label="Status (filtro client-side)"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(0);
                    }}
                  >
                    <option value="ALL">Todos os status</option>
                    <option value="ACTIVE">Ativos</option>
                    <option value="INACTIVE">Inativos</option>
                    <option value="DISCONTINUED">Descontinuados</option>
                  </Select>
                </div>
              </div>

              {pageItems.length === 0 ? (
                <p className="empty-detail" role="status">
                  Nenhum produto encontrado{search || statusFilter !== 'ALL' ? ' para estes filtros' : ''}.
                </p>
              ) : (
                <table className="products-table">
                  <thead>
                    <tr>
                      <th scope="col">SKU</th>
                      <th scope="col">Nome</th>
                      <th scope="col" className="num">Preço venda</th>
                      <th scope="col" className="num">Preço custo</th>
                      <th scope="col">Un.</th>
                      <th scope="col">NCM</th>
                      <th scope="col">Status</th>
                      <th scope="col">
                        <span title="Sem vínculo com saldo de estoque: GET /products não retorna quantidades. A coluna exibirá o saldo quando houver vínculo com GET /inventory/balance.">
                          Estoque ⓘ
                        </span>
                      </th>
                      <th scope="col">
                        <span className="sr-only">Ações</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((p) => {
                      const meta = statusMeta(p.status);
                      return (
                        <tr key={p.id}>
                          <td><strong>{p.sku}</strong></td>
                          <td>{p.name}</td>
                          <td className="num">{formatCurrency(Number(p.price) || 0)}</td>
                          <td className="num">{p.costPrice != null ? formatCurrency(Number(p.costPrice) || 0) : '—'}</td>
                          <td>{p.unit}</td>
                          <td>{p.ncmCode ?? '—'}</td>
                          <td>
                            <Badge variant={meta.variant}>{meta.label}</Badge>
                          </td>
                          <td>
                            <span
                              className="stock-dash"
                              title="Sem vínculo com saldo de estoque — coluna exibirá a quantidade quando houver vínculo com GET /inventory/balance."
                            >
                              —
                            </span>
                          </td>
                          <td className="actions">
                            <button type="button" className="link-action" onClick={() => void navigate({ to: '/inventory/stock' })}>
                              Estoque
                            </button>
                            <button
                              type="button"
                              className="link-action"
                              onClick={() => void handleCopySku(p.sku)}
                              aria-live="polite"
                            >
                              {copiedSku === p.sku ? 'Copiado!' : 'Copiar SKU'}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div className="table-footer">
                <span aria-live="polite">
                  Mostrando {from}–{to} de {filtered.length} (paginação client-side — GET /products retorna array sem total do servidor)
                </span>
                <span className="pager">
                  <button type="button" disabled={safePage === 0} onClick={() => setPage(safePage - 1)} aria-label="Página anterior">
                    ‹
                  </button>
                  <span>
                    {safePage + 1} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setPage(safePage + 1)}
                    aria-label="Próxima página"
                  >
                    ›
                  </button>
                </span>
              </div>
            </CardBody>
          </Card>
        </>
      )}

      <style>{`
        .products-page { padding: 1.5rem; display: flex; flex-direction: column; gap: 1.25rem; }
        .eyebrow { margin: 0 0 0.25rem; font-size: 0.75rem; letter-spacing: 0.08em; text-transform: uppercase; color: #002045; font-weight: 700; }
        .tenant-name { margin: 0; color: #43474e; font-size: 0.85rem; }
        .kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 1rem; }
        .kpi-card { padding: 0.25rem; }
        .kpi-value { margin: 0; font-size: 1.25rem; font-weight: 800; color: #191c1e; }
        .kpi-label { margin: 0.25rem 0; font-size: 12px; color: #43474e; font-weight: 500; font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.02em;}
        .kpi-source { margin: 0; font-size: 0.72rem; color: #74777f; }
        .exec-title { margin: 0; font-size: 1rem; font-weight: 700; color: #191c1e; }
        .exec-subtitle { margin: 0.15rem 0 0; font-size: 0.78rem; color: #43474e; }
        .filters { display: flex; gap: 0.75rem; margin-bottom: 1rem; flex-wrap: wrap; }
        .filter-search { flex: 2 1 280px; }
        .filter-status { flex: 1 1 200px; }
        .products-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .products-table th { text-align: left; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em; color: #43474e; padding: 0.5rem; border-bottom: 1px solid rgba(25, 28, 30, 0.1); position: sticky; top: 0; z-index: 1;}
        .products-table td { padding: 0.55rem 0.5rem; border-bottom: 1px solid rgba(25, 28, 30, 0.06); color: #191c1e; vertical-align: middle; }
        .products-table .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; font-family: "JetBrains Mono", ui-monospace, monospace;}
        .stock-dash { color: #74777f; cursor: help; }
        .actions { white-space: nowrap; }
        .link-action { border: 0; background: none; color: #002045; font-weight: 600; font-size: 0.85rem; cursor: pointer; padding: 0.25rem 0.5rem; border-radius: 4px; white-space: nowrap; }
        .link-action:hover { background: rgba(2,132,199,0.1); }
        .link-action:focus-visible { outline: 2px solid #002045; outline-offset: 2px; }
        .table-footer { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding-top: 0.75rem; font-size: 0.82rem; color: #43474e; flex-wrap: wrap; }
        .pager { display: inline-flex; align-items: center; gap: 0.5rem; }
        .pager button { border: 1px solid rgba(25, 28, 30, 0.15); background: #ffffff; border-radius: 4px; width: 2rem; height: 2rem; cursor: pointer; font-size: 1rem; }
        .pager button:disabled { opacity: 0.4; cursor: default; }
        .error-state { padding: 2rem; text-align: center; color: #ba1a1a; display: flex; flex-direction: column; align-items: center; gap: 0.5rem; }
        .error-detail { font-size: 0.85rem; color: #ba1a1a; }
        .empty-detail { font-size: 0.85rem; color: #43474e; padding: 1rem 0; }
        .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
        @media (max-width: 640px) { .products-page { padding: 1rem; } }
        @media (prefers-reduced-motion: reduce) { .link-action, .pager button { transition: none; } }
      `}</style>
    </div>
  );
}
